import { useEffect, useMemo, useRef, useState } from 'react';
import { MenuItem, Select, Tab, Tabs } from '@mui/material';
import { getReport, getReportRun, ReportDetail } from '../../../Api/Reports/reportsApi';
import { ReportTab } from '../../../Api/Report/reportApi';
import { resolveReportTabs } from '../../../utils/reportTabs';
import { workbookFileName } from '../../../utils/reportWorkbook';
import { PageCard } from '../../Compounds/CardWithTitle';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import Text from '../../Atoms/Text';
import { DownloadExcelButton } from '../Report/DownloadExcelButton';
import { ReportTabsViewer } from '../Report/ReportTabsViewer';
import { useReportWorkbook } from '../Report/useReportWorkbook';
import { formatReportDate, tableTypeLabel } from './reportFormatters';

interface ReportDataViewerProps {
  reportId: string;
}

interface RunData {
  content: Record<string, unknown>[];
  tabs: ReportTab[];
}

const tabsOf = (data: {
  tabs?: ReportTab[];
  view_config: ReportTab['view_config'];
  table_type: string;
  view_name: string;
}): ReportTab[] =>
  resolveReportTabs(data.tabs, {
    tab_name: data.view_name,
    table_type: data.table_type,
    view_config: data.view_config
  });

export const ReportDataViewer = ({ reportId }: ReportDataViewerProps) => {
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [run, setRun] = useState<RunData>({ content: [], tabs: [] });
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState(false);
  const [cardCollapsed, setCardCollapsed] = useState(false);
  // Cache run payloads by run id so re-selecting a run doesn't refetch.
  const runCache = useRef<Record<string, RunData>>({});
  // Bumped on every run fetch / report change so stale or out-of-order responses are ignored.
  const runReqSeq = useRef(0);

  useEffect(() => {
    let active = true;
    runReqSeq.current += 1;
    const load = async () => {
      setLoading(true);
      setCardCollapsed(false);
      setError(false);
      setRunError(false);
      setRunLoading(false);
      runCache.current = {};
      const { data, error: err } = await getReport(reportId);
      if (!active) return;
      if (data) {
        setDetail(data);
        if (data.latest_run) {
          const latest = { content: data.latest_run.content, tabs: tabsOf(data) };
          runCache.current[data.latest_run.id] = latest;
          setSelectedRunId(data.latest_run.id);
          setRun(latest);
        } else {
          setSelectedRunId('');
          setRun({ content: [], tabs: tabsOf(data) });
        }
      } else if (err) {
        setError(true);
        setDetail(null);
      }
      setLoading(false);
    };
    void load();
    return () => {
      active = false;
    };
  }, [reportId]);

  const handleRunSelect = async (runId: string) => {
    // Bump on every selection so this selection owns the loading state and
    // supersedes any in-flight fetch (prevents a stuck spinner / stale content).
    const seq = (runReqSeq.current += 1);
    setSelectedRunId(runId);
    setRunError(false);
    const cached = runCache.current[runId];
    if (cached) {
      setRun(cached);
      setRunLoading(false);
      return;
    }
    setRunLoading(true);
    const { data, error: err } = await getReportRun(reportId, runId);
    // A newer selection or a report change took over — it owns the loading state.
    if (seq !== runReqSeq.current) return;
    if (data) {
      const fetched = { content: data.content, tabs: tabsOf(data) };
      runCache.current[runId] = fetched;
      setRun(fetched);
    } else if (err) {
      setRunError(true);
    }
    setRunLoading(false);
  };

  const selectedRunDate = detail?.runs.find((candidate) => candidate.id === selectedRunId)?.run_date;
  const fileName = useMemo(
    () => workbookFileName(detail?.view_name ?? '', selectedRunDate ? formatReportDate(selectedRunDate) : ''),
    [detail?.view_name, selectedRunDate]
  );
  const workbook = useReportWorkbook(run.tabs, fileName, selectedRunId);

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-16 mt-6">
        <ComponentSpinner />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="bg-white rounded-lg p-12 text-center mt-6">
        <Text
          size="sm14"
          color="grey8">
          We couldn&apos;t load this report. Please try again.
        </Text>
      </div>
    );
  }

  const lastRunDate = detail.latest_run ? formatReportDate(detail.latest_run.run_date) : null;
  const sheetCount = run.tabs.length;
  const reportTabCount = detail.tab_count ?? detail.tabs?.length ?? 1;
  const isCombined = reportTabCount > 1 || sheetCount > 1;
  const headerTabCount = reportTabCount > 1 ? reportTabCount : sheetCount;

  return (
    <div className="mt-6">
      <PageCard
        title={detail.view_name}
        dataCy="report-data-viewer"
        onCollapseClick={setCardCollapsed}
        headerContent={
          isCombined ? (
            <Text
              size="xs"
              color="indigo"
              className="ml-4"
              dataTestId="report-tab-summary">
              {`${headerTabCount} tabs · ${tableTypeLabel(detail.table_type)}`}
            </Text>
          ) : lastRunDate ? (
            <Text
              size="sm14"
              color="indigo"
              className="ml-4"
              dataTestId="report-last-run">
              {`Last Run: ${lastRunDate}`}
            </Text>
          ) : undefined
        }
        cardActions={
          <div className="flex items-center gap-3">
            {isCombined && detail.runs.length > 0 && (
              <Select
                size="small"
                value={selectedRunId}
                onChange={(event) => void handleRunSelect(event.target.value)}
                renderValue={(value) =>
                  `Run: ${formatReportDate(detail.runs.find((candidate) => candidate.id === value)?.run_date ?? '')}`
                }
                inputProps={{ 'aria-label': 'Run' }}
                data-cy="report-run-select">
                {detail.runs.map((candidate) => (
                  <MenuItem
                    key={candidate.id}
                    value={candidate.id}>
                    {formatReportDate(candidate.run_date)}
                  </MenuItem>
                ))}
              </Select>
            )}
            <DownloadExcelButton
              sheetCount={sheetCount}
              disabled={!workbook.ready || workbook.downloading || runLoading || cardCollapsed || !selectedRunId}
              onDownload={() => void workbook.download()}
            />
          </div>
        }>
        <>
          {!isCombined && detail.runs.length > 0 && (
            <Tabs
              value={selectedRunId}
              onChange={(_, value: string) => void handleRunSelect(value)}
              variant="scrollable"
              scrollButtons="auto"
              data-cy="report-run-tabs"
              sx={{
                minHeight: 0,
                marginBottom: '0.5rem',
                '.MuiTab-root': { textTransform: 'none', minHeight: 0 }
              }}>
              {detail.runs.map((candidate) => (
                <Tab
                  key={candidate.id}
                  value={candidate.id}
                  label={formatReportDate(candidate.run_date)}
                />
              ))}
            </Tabs>
          )}
          {runLoading ? (
            <div className="p-16">
              <ComponentSpinner />
            </div>
          ) : runError ? (
            <div className="p-12 text-center">
              <Text
                size="sm14"
                color="grey8">
                We couldn&apos;t load this run. Please try again.
              </Text>
            </div>
          ) : (
            <ReportTabsViewer
              key={selectedRunId}
              tabs={run.tabs}
              content={run.content}
              workbook={workbook}
              toolbarVariant="full"
              reportName={detail.view_name}
            />
          )}
        </>
      </PageCard>
    </div>
  );
};
